import React, { useCallback, useMemo } from 'react';
import {
  Box,
  DraggableList,
  DraggableListGroupPlaceholder,
  ErrorMessage,
  Inscription,
  useDraggableList,
} from '@common-fe/common-fe';

import { DIVIDING_MODE_GROUP_PREFIX, LockboxValidationError, lockboxValidationErrorTitle } from '@/modules/employer/components/Lockbox/lockbox.types';
import LockboxInfoContainer from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxInfoContainer';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useLockboxDividingModeStore } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDividingMode.store';

import AddNewGroupButton from './AddNewGroupButton';

const LockboxDividingModeForm = () => {
  const { groups, activeGroupKey, setGroups, setActiveGroupKey } = useLockboxDividingModeStore();
  const { addGroup, removeGroup } = useDraggableList({ groups, setGroups, newGroupPrefix: DIVIDING_MODE_GROUP_PREFIX });
  const { validationErrors } = useLockboxDetailsStore();

  const errorType = useMemo(() => {
    return validationErrors?.find((error) => error === LockboxValidationError.RECORDS_CANNOT_BE_EMPTY
    || error === LockboxValidationError.MINIMUM_TWO_RECORDS_REQUIRED
    || error === LockboxValidationError.ALL_PAGES_MUST_BE_SELECTED) as LockboxValidationError | undefined;
  }, [validationErrors]);

  const handleRemoveGroup = useCallback((groupKey?: string) => {
    const groupIndex = groups?.findIndex((group) => group.key === groupKey);
    removeGroup(groupKey);
    setActiveGroupKey(groups?.[groupIndex - 1]?.key);
  }, [groups, removeGroup, setActiveGroupKey]);

  const handleAddGroup = useCallback(() => {
    const newGroup = addGroup();
    setActiveGroupKey(newGroup?.key);
  }, [addGroup, setActiveGroupKey]);

  return (
    <LockboxInfoContainer>
      <Box direction="row" align="center" margin={{ bottom: 'spacing12' }}>
        <Inscription
          color="textSecondary"
        >
          Dividing mode
        </Inscription>

        {errorType && (
          <Box  margin={{ left: 'spacing12' }}>
            <ErrorMessage
              testId="dividing-mode"
              errorText={lockboxValidationErrorTitle?.[errorType]}
              errorStyle={{ fontWeight: 500 }}
            />
          </Box>
        )}
      </Box>

      <DraggableList
        testId="dividing-mode"
        groups={groups}
        setGroups={setGroups}
        activeGroupKey={activeGroupKey}
        onRemoveGroup={handleRemoveGroup}
        onGroupClick={setActiveGroupKey}
        placeholder={<DraggableListGroupPlaceholder placeholderText="Select Pages" />}
        isEditMode
      />

      <Box
        margin={{ top: 'spacing12' }}
        pad={{ vertical: 'spacing6', horizontal: 'spacing8' }}
        align="start"
      >
        <AddNewGroupButton title="Add Record" onAddGroup={handleAddGroup} />
      </Box>
    </LockboxInfoContainer>
  );
};

export default LockboxDividingModeForm;
